import { defineComponent as _defineComponent } from 'vue'
import { nextTick, reactive, ref, watch } from 'vue';
import { useStore, useContext } from '@nuxtjs/composition-api';

import { useVuelidate } from '@vuelidate/core';
import {
  useForm,
  useCountries,
  useModalDialog,
  useValidationRules,
} from '~/composables';

type TFormPurpose = 'sales' | 'warranty';
type TFormPurposes = Record<TFormPurpose, any>;
interface IMessageSubject {
  id: TFormPurpose;
  name: string;
}
interface ConsultationForm {
  website_url: string;
  lead_source?: string;
  lang: string;
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  mobile_phone: string;
  country: string;
  business_needs_pain_points_comments: string;
  message_subject: string;
  marketing_yes: boolean;
  gdpr_yes: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ConsultationForm',
  setup(__props) {

const FORM_DEFAULTS = {
  first_name: '',
  last_name: '',
  mobile_phone: '',
  email: '',
  company_name: '',
  country: '',
  business_needs_pain_points_comments: '',
  message_subject: '',
  marketing_yes: false,
  gdpr_yes: false,
};

const store = useStore();
const config = store.getters['config/getConfig'];
const lang: string = store.getters['config/getActiveLanguage'];

const {
  formPending,
  submit,
  recaptcha: $recaptcha,
  isSubmitDisabled,
} = useForm({
  confirm: false,
});

const { $gtm, i18n, nuxtState } = useContext();
const {
  isOpen: infoModalIsOpen,
  open: openInfoModal,
  close: closeInfoModal,
} = useModalDialog('info');

const formData = reactive<ConsultationForm>({
  website_url: config.domain,
  lang,
  ...FORM_DEFAULTS,
});

const {
  required,
  checked,
  email,
  publicEmail,
  phone,
  alphaSpaces,
  errorMessage,
} = useValidationRules();

const v$ = useVuelidate(
  {
    first_name: { required, alphaSpaces },
    last_name: { required, alphaSpaces },
    mobile_phone: { required, phone },
    email: { required, email, publicEmail },
    company_name: { required, alphaSpaces },
    country: { required },
    message_subject: { required },
    business_needs_pain_points_comments: { required },
    marketing_yes: { checked },
    gdpr_yes: { checked },
  },
  formData,
);

const clearForm = () => {
  Object.assign(formData, {
    website_url: config.domain,
    lang,
    ...FORM_DEFAULTS,
  });

  nextTick(() => v$.value.$reset());
};

const isFormStartEventSent = ref(false);
const { getCountriesList } = useCountries();
const countries = getCountriesList();

const messageSubjects: IMessageSubject[] = [
  { id: 'sales', name: i18n.t('consult_subject_sales') as string },
  { id: 'warranty', name: i18n.t('consult_subject_warranty') as string },
];

const submitForm = async () => {
  try {
    const { message_subject: messageSubject, ...formPayload } = formData;
    const formUuidByPurpose: TFormPurposes = {
      sales: nuxtState.config.consultSalesFormUuid,
      warranty: nuxtState.config.consultWarrantyFormUuid,
    };
    const uuid = formUuidByPurpose[messageSubject as TFormPurpose];
    await submit(
      {
        ...formPayload,
        page_url: window.location.href,
        source_description: config.domain,
      },
      uuid,
    );
    $gtm.push({
      event: 'leadform_sent',
    });
    openInfoModal();
    clearForm();
  } catch (error) {
    console.error('Error submitting form', error);
  }
};

const pushFormStartEvent = () => {
  if (!isFormStartEventSent.value) {
    $gtm.push({
      event: 'form_start',
    });
  }
  isFormStartEventSent.value = true;
};

watch(formData, () => {
  pushFormStartEvent();
});

return { __sfc: true,FORM_DEFAULTS, store, config, lang, formPending, submit, $recaptcha, isSubmitDisabled, $gtm, i18n, nuxtState, infoModalIsOpen, openInfoModal, closeInfoModal, formData, required, checked, email, publicEmail, phone, alphaSpaces, errorMessage, v$, clearForm, isFormStartEventSent, getCountriesList, countries, messageSubjects, submitForm, pushFormStartEvent }
}

})